import api from '../../../lib/api';
import type {
  CampaignTestConversation,
  CampaignTestSendRequest,
  CampaignTestSendResult,
  CreateMessagingChannelRequest,
  MessagingChannelPublic,
  UpdateMessagingChannelRequest,
} from '@workspace/types';
import { scopedParams } from './contactAgentScope';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

function withTenant(extra?: Record<string, unknown>, tenantId?: string): Record<string, unknown> | undefined {
  return scopedParams({ ...extra, ...(tenantId ? { tenantId: tenantId.slice(0, 64) } : {}) });
}

export const messagingAdminApi = {
  async listChannels(instanceId: string, tenantId?: string): Promise<MessagingChannelPublic[]> {
    const response = await api.get<ApiResponse<MessagingChannelPublic[]>>('/admin/messaging/channels', {
      params: withTenant({ instanceId: instanceId.slice(0, 64) }, tenantId),
    });
    return requireData(response, 'No se pudieron cargar los canales de campaña');
  },

  async createChannel(
    payload: CreateMessagingChannelRequest,
    tenantId?: string,
  ): Promise<MessagingChannelPublic> {
    const response = await api.post<ApiResponse<MessagingChannelPublic>>(
      '/admin/messaging/channels',
      payload,
      { params: withTenant(undefined, tenantId) },
    );
    return requireData(response, 'No se pudo guardar el canal');
  },

  async updateChannel(
    channelId: string,
    payload: UpdateMessagingChannelRequest,
    tenantId?: string,
  ): Promise<MessagingChannelPublic> {
    const response = await api.patch<ApiResponse<MessagingChannelPublic>>(
      `/admin/messaging/channels/${channelId.slice(0, 64)}`,
      payload,
      { params: withTenant(undefined, tenantId) },
    );
    return requireData(response, 'No se pudo actualizar el canal');
  },

  async deleteChannel(channelId: string, tenantId?: string): Promise<void> {
    await api.delete(`/admin/messaging/channels/${channelId.slice(0, 64)}`, {
      params: withTenant(undefined, tenantId),
    });
  },

  async testSend(
    channelId: string,
    payload: CampaignTestSendRequest,
    tenantId?: string,
  ): Promise<CampaignTestSendResult> {
    const response = await api.post<ApiResponse<CampaignTestSendResult>>(
      `/admin/messaging/channels/${channelId.slice(0, 64)}/test-send`,
      payload,
      { params: withTenant(undefined, tenantId) },
    );
    return requireData(response, 'No se pudo enviar la prueba');
  },
};

export const campaignTestConversationApi = {
  async listWhatsApp(
    channelId: string,
    tenantId?: string,
  ): Promise<{ items: CampaignTestConversation[]; nextCursor?: string }> {
    const response = await api.get<ApiResponse<{ items: CampaignTestConversation[]; nextCursor?: string }>>(
      `/admin/whatsapp/channels/${channelId.slice(0, 64)}/test-conversations`,
      { params: scopedParams(tenantId ? { tenantId } : undefined) },
    );
    return requireData(response, 'No se pudieron cargar las conversaciones de prueba');
  },

  async deleteWhatsApp(
    channelId: string,
    conversationId: string,
    tenantId?: string,
  ): Promise<{ conversationId: string }> {
    const response = await api.delete<ApiResponse<{ conversationId: string }>>(
      `/admin/whatsapp/channels/${channelId.slice(0, 64)}/test-conversations/${conversationId.slice(0, 64)}`,
      { params: scopedParams(tenantId ? { tenantId } : undefined) },
    );
    return requireData(response, 'No se pudo borrar el hilo de prueba');
  },
};
